// How far a module header's chips should give way so the title keeps its row
// (TOM-439, TOM-444): the title never truncates. The watch bento puts the
// Forecast module in a cell narrow enough that "Gust 20 kn · 12:21 AM" cut
// "Forecast · 24 h" to "Fo…". A cell's width depends on the window and the
// composition, so the chips start in full and step down, one detail at a time,
// before paint, only while the title does not fit; a new header width or new
// text starts again in full, as useFittedDensity does for the tank rows.

import { useLayoutEffect, useState } from 'react';

/** Below this, an overrun is float noise in the layout, not a drawn ellipsis. */
const OVERRUN_EPSILON_PX = 0.01;

/**
 * True when the title text is cut: clipped to an ellipsis, or wrapped. With
 * `overrun` measured, it decides the ellipsis: any overrun at all draws one.
 */
export function titleClipped(title: {
  scrollWidth: number;
  clientWidth: number;
  height: number;
  lineHeight: number;
  overrun?: number;
}): boolean {
  const cut =
    title.overrun !== undefined
      ? title.overrun > OVERRUN_EPSILON_PX
      : title.scrollWidth > title.clientWidth + 1;
  return cut || title.height > title.lineHeight * 1.5;
}

/**
 * How far an element's text runs past its own content box, to the subpixel.
 * An ellipsis is drawn for any overrun, and `scrollWidth` rounds one under a
 * pixel away: at 1440×900 in a wide font, Tide's title read "Ti…" 0.39px
 * short of its text, with `scrollWidth` equal to `clientWidth` (TOM-450).
 */
export function textOverrun(el: HTMLElement): number {
  const range = document.createRange();
  range.selectNodeContents(el);
  const css = getComputedStyle(el);
  const box =
    el.getBoundingClientRect().width -
    parseFloat(css.paddingLeft) -
    parseFloat(css.paddingRight) -
    parseFloat(css.borderLeftWidth) -
    parseFloat(css.borderRightWidth);
  return range.getBoundingClientRect().width - box;
}

/** Whether `el` draws an ellipsis over its own text (TOM-450). */
export function ellipsized(el: HTMLElement): boolean {
  return (
    getComputedStyle(el).textOverflow === 'ellipsis' &&
    (el.textContent ?? '').trim() !== '' &&
    textOverrun(el) > OVERRUN_EPSILON_PX
  );
}

/**
 * The chips' attributes at `step` of `steps`: `data-head-wrap` once they take
 * their own row, and `data-head-yielding` while they can still give way. A
 * title cut beside yielding chips is about to fit, and the bento's fit check
 * waits it out (TOM-450).
 */
export function headYieldAttrs(step: number, steps: number): Record<string, ''> {
  return step >= steps ? { 'data-head-wrap': '' } : { 'data-head-yielding': '' };
}

function measure(el: HTMLElement) {
  const css = getComputedStyle(el);
  const lineHeight = parseFloat(css.lineHeight) || parseFloat(css.fontSize) * 1.3;
  return {
    scrollWidth: el.scrollWidth,
    clientWidth: el.clientWidth,
    height: el.getBoundingClientRect().height,
    lineHeight,
    overrun: textOverrun(el),
  };
}

/**
 * How many steps the chips have given way (0 is in full, `steps` the most),
 * and the ref to put on the chips. They step down while the module header's
 * title does not fit its row beside them. `text` is the header's text in full
 * (the title and the chips'), so a change to either is measured again. A
 * callback ref, because the chips appear only once their data arrives.
 */
export function useHeaderYields(
  text: string,
  steps: number = 1
): [step: number, ref: (el: HTMLElement | null) => void] {
  const [chip, setChip] = useState<HTMLElement | null>(null);
  // `width` is the header's width when the chips last started in full.
  const [fit, setFit] = useState<{ text: string; step: number; width: number }>({
    text,
    step: 0,
    width: 0,
  });
  // New text starts again in full.
  const step = fit.text === text ? fit.step : 0;

  useLayoutEffect(() => {
    if (step >= steps || chip === null) return;
    const title = chip
      .closest('.overview-module-head')
      ?.querySelector<HTMLElement>('.overview-module-title-text');
    // While editing the title is the label field, which has its own row rules
    // (TOM-432); only a drawn title is measured.
    if (title === null || title === undefined || title.querySelector('input') !== null) return;
    if (titleClipped(measure(title))) setFit((f) => ({ ...f, text, step: step + 1 }));
  }, [chip, text, step, steps, fit.width]);

  // The header changes width (a resize, the bento engaging or letting go):
  // start again in full, and the effect above steps down again if it must.
  useLayoutEffect(() => {
    const head = chip?.closest<HTMLElement>('.overview-module-head');
    if (head === null || head === undefined || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const width = head.clientWidth;
      setFit((f) => (f.width === width ? f : { text, step: 0, width }));
    });
    observer.observe(head);
    return () => observer.disconnect();
  }, [chip, text]);

  return [step, setChip];
}
